import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@radix-ui/react-select';
import { ParameterData, parameterStatusUtils } from '../types/ParameterData';
import { ParameterStatusBadge } from './ParameterStatusBadge';
import { ParametersTrendGraph } from './ParametersTrendGraph';

interface Props {
  values: ParameterData[];
  onInfoClick?: () => void;
}

export const ParametersTrendCard: React.FC<Props> = ({
  values,
  onInfoClick,
}) => {
  const labName =
    values.length === 1 ? "1 laboratorio" : `${values.length} laboratori`;

  const averageRange = {
    min: Number(
      (
        values.reduce((acc, curr) => acc + curr.range.min, 0) / values.length
      ).toFixed(2),
    ),
    max: Number(
      (
        values.reduce((acc, curr) => acc + curr.range.max, 0) / values.length
      ).toFixed(2),
    ),
  };
  const unit = values[0].unit;
  const recentValues = values.slice(-3);

  const recentStatusData = recentValues.map((val) => {
    const { status } = parameterStatusUtils.calculateStatus(
      val.value,
      averageRange.min,
      averageRange.max,
      val.date
    );
    const simplifiedStatus = parameterStatusUtils.simplifyStatus(status);
    const color = parameterStatusUtils.getColorForSimpleStatus(simplifiedStatus);

    return {
      value: val.value,
      date: val.date,
      status: simplifiedStatus,
      color,
    };
  });

  const parameterName =
    parameterStatusUtils.getNameForId(values[0].name.toUpperCase()) ??
    values[0].name;

  return (
    <Card className='shadow-md'>
      <CardContent>
        <Accordion type='single' collapsible>
          <AccordionItem value='item-1'>
            <AccordionTrigger className='py-2'>
              <div className='flex justify-between w-full items-center'>
                <div className='flex flex-col gap-2'>
                  <p className='text-lg font-semibold'>
                    {(() => {
                      const displayName = parameterName;
                      const match = displayName.match(/^(.*?)\s*(\([^)]*\).*)$/);
                      if (match) {
                        const [, main, rest] = match;
                        return (
                          <>
                            {main}{' '}
                            <span className="text-sm font-normal">{rest}</span>
                          </>
                        );
                      }
                      return displayName;
                    })()}
                  </p>
                  <div className='flex gap-4 items-center'>
                    <ParameterStatusBadge
                      value={recentStatusData[recentStatusData.length - 1].value}
                      range={averageRange}
                      date={values[values.length - 1].date}
                    />
                    <div className='flex gap-1 items-center'>
                      <p className='text-xl'>{recentStatusData[recentStatusData.length - 1].value}</p>
                      <p className='text-base text-muted'>{unit}</p>
                    </div>
                  </div>
                  <div className='flex gap-3 items-center'>
                    <p className='text-sm font-medium'>{labName}</p>
                    <div>|</div>
                    <div className='flex gap-1 items-center'>
                      <div className='flex items-center'>
                        <p className='text-sm'>{averageRange.min}</p>
                        <p className='text-sm'>-</p>
                        <p className='text-sm'>{averageRange.max}</p>
                      </div>
                      <p className='text-sm'>{unit}</p>
                    </div>
                  </div>
                </div>
                <div className='flex gap-2 items-center'>
                  {recentStatusData.map((entry, index) => (
                    <div
                      key={entry.date || index}
                      className={`w-3 h-3 rounded-full ${index === recentStatusData.length - 1 ? "" : index === 0 ? "" : ""
                        } ${entry.color}`}
                    ></div>
                  ))}
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className='p-0 space-y-[15px]'>
              <div>
              <Separator className='w-full h-[1px] bg-gray-200 mt-2' />
              {values.length > 1 ? (
                <ParametersTrendGraph values={values} range={averageRange} />
              ) : (
                <p className='text-sm text-gray-500'>
                  Non ci sono sufficienti dati per mostrare il grafico
                </p>
              )}
              </div>
              {onInfoClick && (
              <div
                className='text-primary font-bold text-[12px] cursor-pointer'
                onClick={onInfoClick}
              >
                Nozioni generali
              </div>
            )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </Card>
  );
};
